// What one booking / order is made of — ONE place for the rule, used by
// Expenses, Services, the Brief Report and the Dashboard.
//
// A hotel booking is not one amount: 7 Deluxe rooms × 3,500 × 2 nights, the
// hall rent, a dinner of 900 a head for AT LEAST 100 people, a wine permit …
// Each is a LINE (Evt_CostLines), owned by an expense or a service
// (ParentType 'Expense' | 'Service', ParentId).
//
//   Basis 'Fixed'  the line's Amount (hall rent, a permit)
//   Basis 'Qty'    Quantity × Rate × Days; Days = the nights between FromDate
//                  and ToDate when both are given (check-in 26, out 28 = 2)
//   LiveSource     ties the quantity to a live count of the event:
//                    Headcount   persons at FunctionId (functionHeadcount)
//                    Households  one per card at FunctionId (sweets)
//                    Cards       cards for the whole event + extra cards (gifts)
//   ChargeRule     'Higher' - the higher of the booked minimum and the live
//                  count is charged (a dinner booked for 100 that 134 attend);
//                  'Booked' - the booked quantity is charged and the live
//                  count is shown beside it (288 gifts ordered, 301 needed).
//                  A line with nothing booked yet always follows the count.
import { functionHeadcount, functionCards, eventCards } from './headcount';
import { needsCard, isPastFunction } from './helpers';

export const LINE_SOURCES = [
  { value: '', label: 'Not linked', unit: '' },
  { value: 'Headcount', label: 'Persons at the function', unit: 'persons', needsFunction: true },
  { value: 'Households', label: 'Households at the function (one per card)', unit: 'households', needsFunction: true },
  { value: 'Cards', label: 'Cards for the whole event (with extra cards)', unit: 'cards' },
];
export const CHARGE_RULES = [
  { value: 'Higher', label: 'Higher of booked and live' },
  { value: 'Booked', label: 'Booked quantity only' },
];
export const EXPENSE_STATUSES = ['Planned', 'Ordered', 'Booked', 'Done'];
export const sourceOf = (v) => LINE_SOURCES.find(s => s.value === String(v || '')) || LINE_SOURCES[0];

const num = (v) => Number(v || 0) || 0;
const round2 = (n) => Math.round(n * 100) / 100;

export const blankLine = (over = {}) => ({
  key: `n${Math.random().toString(36).slice(2, 9)}`, CostLineId: null,
  Title: '', Category: '', FunctionId: '', Basis: 'Qty', Quantity: '', Rate: '', Days: '1',
  FromDate: '', ToDate: '', LiveSource: '', ChargeRule: 'Higher', Amount: '', Notes: '',
  // A quotation's two extras: "if needed" and "as per requirement".
  Optional: 0, RateMax: '',
  ...over,
});

// Nights between two YYYY-MM-DD dates (0 when either is missing or out of order).
export function nightsBetween(from, to) {
  if (!from || !to) return 0;
  const a = Date.parse(`${String(from).slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${String(to).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return 0;
  return Math.round((b - a) / 86400000);
}

export const lineDays = (l) => nightsBetween(l.FromDate, l.ToDate) || Math.max(1, num(l.Days) || 1);

// The live counts of an event, as one function: count(source, functionId)
// → a number, or null when the line is not linked (or the link is incomplete).
export function makeCounter({ guests, cardTypes, functions }) {
  const cache = new Map();
  const open = (functions || []).filter(f => !isPastFunction(f)).map(f => String(f.FunctionId));
  return (source, functionId) => {
    const src = sourceOf(source);
    if (!src.value) return null;
    if (src.needsFunction && !functionId) return null;
    const key = `${src.value}:${src.needsFunction ? functionId : ''}`;
    if (!cache.has(key)) {
      let n = 0;
      if (src.value === 'Headcount') n = functionHeadcount(guests, functionId, cardTypes).persons;
      else if (src.value === 'Households') n = functionCards(guests, functionId, cardTypes, needsCard).households;
      else if (src.value === 'Cards') n = eventCards(guests, open, needsCard).cards;
      cache.set(key, n);
    }
    return cache.get(key);
  };
}

// One line worked out → { days, booked, live, qty, amount, gap, linked }.
//   gap = live − booked (more needed than booked/ordered when > 0).
export function lineMath(l, count) {
  // "If needed" (a CO2 gun for the baraat) and "as per requirement" (varmala
  // 4,000 to 6,000). `amount` is always what is OWED today - an optional line
  // owes nothing until it is taken, a ranged one owes its floor - and
  // `maxAmount` is what it could come to. The totals carry both.
  const optional = Number(l.Optional || 0) === 1;
  const max = num(l.RateMax);
  if (String(l.Basis) === 'Fixed') {
    const amount = round2(num(l.Amount));
    return {
      fixed: true, days: 1, booked: 0, live: null, qty: 0, gap: 0, linked: false,
      optional, ranged: max > amount, amount,
      maxAmount: round2(Math.max(amount, max)),
    };
  }
  const days = lineDays(l);
  const booked = num(l.Quantity);
  const live = l.LiveSource && count ? count(l.LiveSource, l.FunctionId) : null;
  const linked = live != null;
  let qty = booked;
  if (linked && (String(l.ChargeRule) !== 'Booked' || booked <= 0)) qty = Math.max(booked, live);
  const rate = num(l.Rate);
  return {
    fixed: false, days, booked, live, qty, linked,
    optional, ranged: max > rate,
    amount: round2(qty * rate * days),
    maxAmount: round2(qty * Math.max(rate, max) * days),
    gap: linked ? live - booked : 0,
  };
}

// What the lines come to. `total` is what is OWED - the lines taken, each at
// the floor of its range - and `upTo` is the same list with every "if needed"
// line taken and every range at its ceiling: the two numbers a quotation is
// read with ("35,000 agreed, up to 55,000 if both extras are taken").
export function linesSummary(lines, count) {
  let total = 0; let upTo = 0; let optional = 0; let ranged = 0;
  (lines || []).forEach((l) => {
    const m = lineMath(l, count);
    upTo += m.maxAmount;
    if (m.optional) optional += m.maxAmount; else total += m.amount;
    if (m.ranged && !m.optional) ranged += m.maxAmount - m.amount;
  });
  return { total: round2(total), upTo: round2(upTo), optional: round2(optional), ranged: round2(ranged) };
}
export const linesTotal = (lines, count) => linesSummary(lines, count).total;

// The lines of one expense / service, in their order.
export const linesOf = (lines, type, id) => (lines || [])
  .filter(l => l.ParentType === type && String(l.ParentId) === String(id))
  .sort((a, b) => num(a.SortOrder) - num(b.SortOrder) || num(a.CostLineId) - num(b.CostLineId));

// "7 × ₹3,500 × 2 nights" — how a line's amount is made, for lists and exports.
export function lineBasisText(l, m, fmt) {
  if (m.fixed) return 'Fixed';
  const parts = [`${m.qty} × ${fmt(l.Rate)}`];
  if (m.days > 1) parts.push(`${m.days} ${l.FromDate && l.ToDate ? 'nights' : 'days'}`);
  return parts.join(' × ');
}

// "if needed" / "up to 12,000" - what a quotation's line says about itself.
export function lineRangeText(l, m, fmt) {
  const parts = [];
  if (m.optional) parts.push('if needed');
  if (m.ranged) parts.push(`up to ${fmt(m.maxAmount)}`);
  return parts.join(' · ');
}

// "Booked 100 · live 134 · charged 134" — the line's numbers side by side.
export function lineCountText(l, m) {
  if (m.fixed || !m.linked) return '';
  const unit = sourceOf(l.LiveSource).unit;
  const parts = [`${String(l.ChargeRule) === 'Booked' ? 'ordered' : 'min'} ${m.booked}`, `${unit} now ${m.live}`];
  if (m.gap > 0 && String(l.ChargeRule) === 'Booked' && m.booked > 0) parts.push(`${m.gap} short`);
  else if (m.gap < 0 && String(l.ChargeRule) === 'Booked') parts.push(`${-m.gap} spare`);
  else if (m.qty !== m.booked) parts.push(`charged ${m.qty}`);
  return parts.join(' · ');
}

// A due date within `days` days from today (or already past) with money owed.
export function isDueSoon(dueDate, balance, today, days = 7) {
  if (!dueDate || !(balance > 0)) return false;
  return nightsBetween(today, String(dueDate).slice(0, 10)) <= days;
}

// A line as it is sent to the API.
export function linePayload(l, m) {
  const fixed = String(l.Basis) === 'Fixed';
  return {
    Title: String(l.Title || '').trim(), Category: String(l.Category || '').trim() || null,
    FunctionId: l.FunctionId ? Number(l.FunctionId) : null, Basis: fixed ? 'Fixed' : 'Qty',
    Quantity: fixed ? 0 : num(l.Quantity), Rate: fixed ? 0 : num(l.Rate),
    Days: fixed ? 1 : Math.max(1, num(l.Days) || 1),
    FromDate: !fixed && l.FromDate ? String(l.FromDate).slice(0, 10) : null,
    ToDate: !fixed && l.ToDate ? String(l.ToDate).slice(0, 10) : null,
    LiveSource: !fixed && l.LiveSource ? l.LiveSource : null,
    ChargeRule: l.ChargeRule === 'Booked' ? 'Booked' : 'Higher',
    Optional: Number(l.Optional || 0) === 1 ? 1 : 0,
    RateMax: num(l.RateMax) || null,
    Amount: m.amount, Notes: String(l.Notes || '').trim() || null,
  };
}

// A saved line → form values.
export const lineToForm = (l) => blankLine({
  key: `l${l.CostLineId}`, CostLineId: l.CostLineId, Title: l.Title || '', Category: l.Category || '',
  FunctionId: l.FunctionId ? String(l.FunctionId) : '', Basis: l.Basis === 'Fixed' ? 'Fixed' : 'Qty',
  Quantity: num(l.Quantity) ? String(num(l.Quantity)) : '', Rate: num(l.Rate) ? String(num(l.Rate)) : '',
  Days: String(Math.max(1, num(l.Days) || 1)),
  FromDate: l.FromDate ? String(l.FromDate).slice(0, 10) : '', ToDate: l.ToDate ? String(l.ToDate).slice(0, 10) : '',
  LiveSource: l.LiveSource || '', ChargeRule: l.ChargeRule === 'Booked' ? 'Booked' : 'Higher',
  Amount: String(l.Basis) === 'Fixed' && num(l.Amount) ? String(num(l.Amount)) : '', Notes: l.Notes || '',
  Optional: Number(l.Optional || 0) === 1 ? 1 : 0,
  RateMax: num(l.RateMax) ? String(num(l.RateMax)) : '',
});

// Save the edited lines of one parent: add new ones, update the rest, retire
// the ones that were removed (Valid 0). `api` = { add, update }.
export async function saveLines({ api, lines, saved, parentType, parentId, eventId, count, me }) {
  const keep = new Set();
  let order = 0;
  for (const l of lines) {
    if (!String(l.Title || '').trim()) continue;
    order += 10;
    const data = { ...linePayload(l, lineMath(l, count)), SortOrder: order, UpdatedBy: me };
    if (l.CostLineId) {
      keep.add(String(l.CostLineId));
      await api.update({ data, where: `CostLineId = ${Number(l.CostLineId)}` });
    } else {
      await api.add({ ...data, EventId: eventId, ParentType: parentType, ParentId: Number(parentId), Valid: 1, CreatedBy: me });
    }
  }
  for (const s of saved || []) {
    if (!keep.has(String(s.CostLineId))) {
      await api.update({ data: { Valid: 0, UpdatedBy: me }, where: `CostLineId = ${Number(s.CostLineId)}` });
    }
  }
}

// A quotation is not a booking: a service sitting at Enquiry is a PRICE
// SOMEBODY SENT, and until it is confirmed it belongs nowhere near what is
// owed. Every money screen reads this, so three quotations for one function
// cannot quietly add a lakh to the balance.
export const isQuotation = (sv) => String(sv && sv.Status ? sv.Status : '') === 'Enquiry';
export const isLiveService = (sv) => !isQuotation(sv) && String(sv && sv.Status ? sv.Status : '') !== 'Cancelled'
  && Number(sv && sv.Valid !== undefined && sv.Valid !== null ? sv.Valid : 1) !== 0;

// What the vendor took off: 40,000 quoted, 5,000 off, 35,000 agreed. Kept as
// its own number because the discount is the thing that was negotiated and the
// next vendor is asked for the same.
export const discountOf = (p) => num(p && p.Discount);

// One booking / quotation as money: { quoted, discount, total, upTo, optional }
//   quoted   the lines as they stand, before the discount
//   total    what is owed - after the discount, "if needed" lines left out
//   upTo     everything taken, every range at its ceiling, less the discount
export function parentMoney(parent, ownLines, count, fallback = 0) {
  const discount = discountOf(parent);
  if (!ownLines || !ownLines.length) {
    const quoted = round2(num(fallback));
    return {
      quoted, discount, lines: false, optional: 0, ranged: 0,
      total: round2(quoted - discount), upTo: round2(quoted - discount),
    };
  }
  const sum = linesSummary(ownLines, count);
  return {
    quoted: sum.total, discount, lines: true,
    total: round2(sum.total - discount),
    upTo: round2(sum.upTo - discount),
    optional: sum.optional, ranged: sum.ranged,
  };
}

// The live total of an expense: its lines, else rate × persons (persons typed
// or a function's headcount), else the typed amount.
export function expenseTotal(e, ownLines, count) {
  if (ownLines && ownLines.length) return round2(linesTotal(ownLines, count) - discountOf(e));
  if (e.AmountType === 'Lines') return round2(num(e.TotalAmount) - discountOf(e));   // lines not loaded: as saved
  if (e.AmountType === 'PerPerson') {
    const persons = e.PersonsSource === 'Headcount' && e.PersonsFunctionId
      ? (count ? count('Headcount', e.PersonsFunctionId) || 0 : 0) : num(e.Persons);
    return round2(num(e.Rate) * persons);
  }
  return num(e.TotalAmount);
}
export const serviceTotal = (sv, ownLines, count) => round2(
  ((ownLines && ownLines.length) ? linesTotal(ownLines, count) : num(sv.TotalAmount)) - discountOf(sv));

// What falls due within `days` days (or is overdue): { amount, items }.
// Expenses need PaidAmount (GetAllEvt_ExpensesByEventId), services AdvancePaid.
export function dueSoonSummary({ expenses, services, lines, count, today, days = 7 }) {
  let amount = 0;
  let items = 0;
  const add = (due, balance) => { if (isDueSoon(due, balance, today, days)) { amount += balance; items += 1; } };
  (expenses || []).forEach(e => add(e.DueDate, round2(expenseTotal(e, linesOf(lines, 'Expense', e.ExpenseId), count) - num(e.PaidAmount))));
  (services || []).filter(isLiveService)
    .forEach(sv => add(sv.DueDate, round2(serviceTotal(sv, linesOf(lines, 'Service', sv.ServiceId), count) - num(sv.AdvancePaid))));
  return { amount: round2(amount), items };
}
